import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useUsage } from '../hooks/useUsage';
import { fetchUserImages, deleteGeneratedImage } from '../lib/usage';
import { GeneratedImage } from '../types';
import UsageBar from '../components/UsageBar';
import Toast, { useToast } from '../components/Toast';
import ImageModal from '../components/ImageModal';
import AlertDialog from '../components/AlertDialog';

const planBadgeClass: Record<string, string> = {
    free: 'badge-free',
    creator: 'badge-creator',
    pro: 'badge-pro',
    lifetime: 'bg-gradient-to-r from-amber-400 to-amber-600 text-white px-3 py-1 rounded-full font-black text-[10px] uppercase tracking-widest shadow-lg shadow-amber-500/20',
};

const Dashboard: React.FC = () => {
    const { user } = useAuthContext();
    const { plan, isPremium, isFree, expiresAt, daysRemaining, loading: profileLoading, fetchProfile } = useProfile();
    const { usage, dailyLimit } = useUsage();
    const { toast, showToast, hideToast } = useToast();
    const [searchParams] = useSearchParams();

    const [images, setImages] = useState<GeneratedImage[]>([]);
    const [imagesLoading, setImagesLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null);

    // Alert Dialog state
    const [alertConfig, setAlertConfig] = useState<{
        isOpen: boolean;
        imageToDelete: GeneratedImage | null;
        isDeleting: boolean;
    }>({
        isOpen: false,
        imageToDelete: null,
        isDeleting: false
    });

    useEffect(() => {
        if (searchParams.get('upgraded') === 'true') {
            const upgradedPlan = searchParams.get('plan');
            if (upgradedPlan === 'lifetime') {
                showToast('🎉🚀 Lifetime access activated! You now have unlimited access to Share Visuals — forever!', 'success');
            } else if (upgradedPlan === 'pro') {
                showToast('🎉 Pro plan activated! You now have full access to batch generation, ZIP downloads, and more.', 'success');
            } else if (upgradedPlan === 'creator') {
                showToast('🎉 Creator plan activated! Unlimited generations with no watermark.', 'success');
            } else {
                showToast('🎉 Subscription activated! Welcome to your new plan.', 'success');
            }
        }
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!user) return;
        setImagesLoading(true);
        fetchUserImages(user.id, 20).then((data) => {
            setImages(data as GeneratedImage[]);
            setImagesLoading(false);
        });
    }, [user]);

    const handleDeleteClick = (image: GeneratedImage) => {
        setAlertConfig({
            isOpen: true,
            imageToDelete: image,
            isDeleting: false
        });
    };

    const handleConfirmDelete = async () => {
        const image = alertConfig.imageToDelete;
        if (!image) return;

        setAlertConfig(prev => ({ ...prev, isDeleting: true }));
        const ok = await deleteGeneratedImage(image.id, image.image_url);

        if (ok) {
            setImages((prev) => prev.filter((i) => i.id !== image.id));
            showToast('Image deleted permanentely', 'info');
            if (selectedImage?.id === image.id) setSelectedImage(null);
        } else {
            showToast('Failed to delete image', 'error');
        }

        setAlertConfig({
            isOpen: false,
            imageToDelete: null,
            isDeleting: false
        });
    };

    if (profileLoading) {
        return (
            <div className="min-h-screen bg-dark-950 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin shadow-glow" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-dark-950 text-white">
            <div className="container-page pt-32 pb-8 space-y-8">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-slide-up">
                    <div>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            Dashboard
                            <button
                                onClick={() => fetchProfile()}
                                className="group p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
                                title="Refresh plan status"
                                aria-label="Refresh plan status"
                            >
                                <svg className={`w-4 h-4 text-dark-400 group-hover:text-primary-400 ${profileLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" aria-hidden="true" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            </button>
                        </h1>
                        <div className="flex items-center gap-2 mt-1">
                            <p className="text-dark-400 text-sm italic font-medium">
                                Ready to ship some stunning visuals today?
                            </p>
                            <span className="text-dark-600 font-bold text-[10px]">|</span>
                            <span className="text-primary-400/80 text-xs font-bold">{user?.email}</span>
                            {plan === 'lifetime' && (
                                <span className="ml-2 px-2 py-0.5 rounded-full bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/30 text-orange-400 text-[9px] font-black uppercase tracking-widest flex items-center gap-1 shadow-[0_0_10px_rgba(249,115,22,0.2)]">
                                    🔥 Founder
                                </span>
                            )}
                        </div>
                    </div>
                    <Link
                        to="/generator"
                        id="create-new-btn"
                        className="btn-primary inline-flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Create New Visual
                    </Link>
                </div>



                {/* Expiry warning */}
                {isPremium && plan !== 'lifetime' && daysRemaining !== null && daysRemaining <= 7 && (
                    <div className="bg-orange-500/10 border border-orange-500/50 rounded-2xl p-4 flex items-center gap-4 animate-fade-in">
                        {/* ... (existing icon) ... */}
                        <div className="flex-1">
                            <p className="text-orange-200 font-semibold">Your plan expires in {daysRemaining} days!</p>
                            <Link to="/upgrade" className="text-orange-400 text-sm hover:text-orange-300 underline underline-offset-4">
                                Renew now to keep premium access
                            </Link>
                        </div>
                    </div>
                )}

                {/* Stats row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Plan */}
                    <div className="card relative group overflow-hidden">
                        <p className="text-dark-400 text-xs uppercase tracking-wider font-semibold mb-3">Current Plan</p>
                        <div className="flex items-center justify-between">
                            <span className={`${planBadgeClass[plan]} capitalize`}>
                                {plan}
                            </span>
                            {isFree ? (
                                <Link to="/upgrade" className="text-primary-400 text-sm hover:text-primary-300 font-medium">
                                    Upgrade →
                                </Link>
                            ) : expiresAt && plan !== 'lifetime' ? (
                                <span className="text-dark-400 text-xs font-bold">
                                    Renews: {expiresAt.toLocaleDateString()}
                                </span>
                            ) : plan === 'lifetime' ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-amber-400 text-[10px] font-black uppercase tracking-widest bg-amber-400/10 px-2 py-0.5 rounded-lg border border-amber-400/20">
                                        Forever Access
                                    </span>
                                    <span className="text-orange-500 text-[10px] font-black uppercase tracking-widest bg-orange-500/10 px-2 py-0.5 rounded-lg border border-orange-500/30 flex items-center gap-1 shadow-[0_0_10px_rgba(249,115,22,0.15)]">
                                        🔥 Founder Badge
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* Usage */}
                    <div className="card">
                        <p className="text-dark-400 text-xs uppercase tracking-wider font-semibold mb-3">Today's Usage</p>
                        <UsageBar
                            used={usage?.count ?? 0}
                            limit={dailyLimit}
                            plan={plan}
                        />
                    </div>

                    {/* Total images */}
                    <div className="card">
                        <p className="text-dark-400 text-xs uppercase tracking-wider font-semibold mb-3">Images Shipped</p>
                        <div className="flex items-end gap-2">
                            <span className="text-4xl font-extrabold text-white">{images.length}</span>
                            <span className="text-dark-400 text-sm mb-1">total</span>
                        </div>
                    </div>
                </div>

                {/* Upgrade CTA for free users */}
                {plan === 'free' && (
                    <div className="bg-gradient-hero border border-primary-500/20 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-glow">
                        <div>
                            <h3 className="text-white font-bold text-lg">Ship like a Pro</h3>
                            <p className="text-dark-300 text-sm mt-1">
                                Upgrade to Pro to remove watermarks and ship unlimited visuals.
                            </p>
                        </div>
                        <Link
                            to="/upgrade"
                            id="dashboard-upgrade-btn"
                            className="btn-accent shrink-0 whitespace-nowrap"
                        >
                            View Plans
                        </Link>
                    </div>
                )}

                {/* Image Gallery */}
                <div>
                    <h2 className="text-lg font-semibold text-white mb-4">Your Recent Ships</h2>

                    {imagesLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin shadow-glow" />
                        </div>
                    ) : images.length === 0 ? (
                        <div className="card flex flex-col items-center justify-center py-20 text-center">
                            <div className="w-16 h-16 rounded-2xl bg-dark-700 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-dark-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-white font-semibold text-lg">No ships yet</h3>
                            <p className="text-dark-400 text-sm mt-1">You haven't shipped any visuals yet.</p>
                            <Link to="/generator" className="btn-primary mt-5">
                                Start Shipping
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {images.map((img) => (
                                <div
                                    key={img.id}
                                    className="group relative bg-dark-800 rounded-xl overflow-hidden border border-dark-700 hover:border-primary-500/40 hover:shadow-glow transition-all duration-200 cursor-pointer"
                                    onClick={() => setSelectedImage(img)}
                                >
                                    <img
                                        src={img.image_url}
                                        alt={img.content}
                                        className="w-full aspect-square object-cover"
                                        loading="lazy"
                                    />
                                    {/* Hover overlay */}
                                    <div className="absolute inset-0 bg-dark-900/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3">
                                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white scale-90 group-hover:scale-100 transition-transform">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        </div>
                                        <span className="text-white text-[10px] font-medium uppercase tracking-widest opacity-70">View Details</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {selectedImage && (
                <ImageModal
                    image={selectedImage}
                    onClose={() => setSelectedImage(null)}
                    onDelete={handleDeleteClick}
                />
            )}

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alertConfig.isOpen}
                title="Delete Image?"
                message="Are you sure you want to delete this image? This action will remove it permanently from the cloud and cannot be undone."
                confirmLabel="Delete Permanently"
                cancelLabel="Keep Image"
                onConfirm={handleConfirmDelete}
                onCancel={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))}
                loading={alertConfig.isDeleting}
            />

            {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        </div>
    );
};

export default Dashboard;
